import React from 'react';
import { Box, Typography } from '@mui/material';
import ScrollFadeWrapper from '../../ScrollFadeWrapper/ScrollFadeWrapper';
import MobileComparisonAccordionTable from './MobileComparisonAccordionTable';
import Image from '../../Image/Image';

const KEYPOINT_VECTOR_URL =
  'https://assets.askhippo.co.za/Vectors/KeyPointVector.svg';
const CARD_SECTION_GAP = '24px';

const MedicalInsuranceSeoDetailModuleMobileComponent = ({ data }) => {
  const { title, description, footer, cards = [] } = data || {};
  const html = (value) => ({ __html: typeof value === 'string' ? value : '' });

  return (
    <Box
      sx={{
        width: '100%',
        maxWidth: '100vw',
        backgroundColor: '#E3E6E8',
        borderRadius: '48px',
        padding: '32px 16px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        boxSizing: 'border-box',
      }}
    >
      <Typography
        sx={{
          fontFamily: 'Inter, sans-serif',
          fontWeight: 700,
          fontSize: '2rem',
          lineHeight: '2.125rem',
          letterSpacing: '-0.03em',
          color: '#162F3B',
          textAlign: 'center',
          whiteSpace: 'pre-line',
          width: '100%',
          px: { xs: '24px', sm: '20px', md: '16px' },
          overflowWrap: 'break-word',
          wordBreak: 'break-word',
          boxSizing: 'border-box',
        }}
        component="div"
        dangerouslySetInnerHTML={html(title)}
      />

      <Typography
        sx={{
          fontFamily: 'Inter, sans-serif',
          fontWeight: 400,
          fontSize: '1.25rem',
          lineHeight: '1.5rem',
          letterSpacing: '-0.03em',
          color: '#162F3B',
          textAlign: 'center',
          mt: '16px',
          mb: '40px',
          whiteSpace: 'pre-line',
          alignSelf: 'stretch',
          boxSizing: 'border-box',
        }}
        component="div"
        dangerouslySetInnerHTML={html(description)}
      />

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: '16px',
          width: '100%',
        }}
      >
        {cards.map((card, index) => (
          <ScrollFadeWrapper key={card.number} delay={index * 0.1}>
            <Box
              sx={{
                width: '100%',
                backgroundColor: '#FFFFFF',
                borderRadius: '34px',
                padding: '32px 16px',
                display: 'flex',
                flexDirection: 'column',
                gap: CARD_SECTION_GAP,
                boxShadow: '0px 6px 16px #162F3B1F, 0px 1px 2px #A2ACB11F',
                boxSizing: 'border-box',
              }}
            >
              <Box
                sx={{
                  position: 'relative',
                  width: '80px',
                  height: '80px',
                }}
              >
                <Image
                  width={80}
                  height={80}
                  src={KEYPOINT_VECTOR_URL}
                  alt="Key point illustration"
                  sx={{ width: '100%', height: '100%' }}
                />
                <Typography
                  sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    fontFamily: 'Pally, Inter',
                    fontWeight: 700,
                    fontSize: '2.125rem',
                    lineHeight: '2.125rem',
                    color: '#162F3B',
                  }}
                >
                  {card.number}
                </Typography>
              </Box>

              <Typography
                sx={{
                  fontWeight: 700,
                  fontSize: '1.25rem',
                  lineHeight: '1.5rem',
                  color: '#162F3B',
                  letterSpacing: '-0.03em',
                }}
                component="div"
                dangerouslySetInnerHTML={html(card.title)}
              />

              <Box
                sx={{
                  fontWeight: 400,
                  fontSize: '1rem',
                  lineHeight: '1.5rem',
                  color: '#162F3B',
                  letterSpacing: '-0.002em',
                  whiteSpace: 'pre-line',
                  '& a': {
                    color: '#C7CA00',
                    fontWeight: 700,
                    textDecoration: 'none',
                  },
                  '& a:hover': {
                    textDecoration: 'underline',
                  },
                  '& p': { m: 0, mb: '1em' },
                  '& p:last-of-type': { mb: 0 },
                  '& ul': { m: 0, mt: '1em', pl: '20px' },
                  '& li': { mb: '0.5em' },
                }}
                component="div"
                dangerouslySetInnerHTML={html(card.description)}
              />

              {card.image && (
                <>
                  <Box
                    sx={{
                      backgroundColor: '#F3F6F6',
                      height: '267px',
                      borderRadius: '16px',
                      width: '100%',
                    }}
                  />
                  <Typography
                    sx={{
                      fontWeight: 600,
                      fontSize: '0.875rem',
                      lineHeight: '1.25rem',
                      color: '#162F3B',
                      letterSpacing: '-0.001em',
                    }}
                    component="div"
                    dangerouslySetInnerHTML={html(card.caption)}
                  />
                </>
              )}

              {card.table && (
                <>
                  <MobileComparisonAccordionTable
                    table={card.table}
                    defaultOpenIndex={0}
                  />
                  {card.table?.secondaryTable ? (
                    <Box sx={{ mt: '16px' }}>
                      <MobileComparisonAccordionTable
                        table={card.table.secondaryTable}
                        defaultOpenIndex={0}
                      />
                    </Box>
                  ) : null}
                </>
              )}

              {card.strongText && (
                <Typography
                  sx={{ fontWeight: 700, fontSize: '1rem', color: '#162F3B' }}
                  component="div"
                  dangerouslySetInnerHTML={html(card.strongText)}
                />
              )}

              {card.paragraph && (
                <Typography
                  sx={{
                    fontWeight: 400,
                    fontSize: '1rem',
                    lineHeight: '1.5rem',
                    color: '#162F3B',
                    letterSpacing: '-0.002em',
                    whiteSpace: 'pre-line',
                  }}
                  component="div"
                  dangerouslySetInnerHTML={html(card.paragraph)}
                />
              )}
            </Box>
          </ScrollFadeWrapper>
        ))}
      </Box>

      {footer ? (
        <Typography
          sx={{
            textAlign: 'center',
            fontFamily: 'Inter, sans-serif',
            fontSize: '0.9375rem',
            fontWeight: 400,
            lineHeight: '1.375rem',
            letterSpacing: '-0.02em',
            color: '#162F3B',
            mt: '24px',
            maxWidth: '100%',
          }}
        >
          {footer}
        </Typography>
      ) : null}
    </Box>
  );
};

export default MedicalInsuranceSeoDetailModuleMobileComponent;
